import React, { useState, useMemo } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, Clock, CheckCircle, XCircle, Users, Trash2, FileText, MapPin, UserCheck, ChevronDown, Check, X, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarView } from './ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { DraggableModal } from './ui/draggable-modal';
import { SearchableSelect } from './ui/SearchableSelect';
import apiClient from '../api/client';
import { toast } from 'sonner';
import { useRoleFlags } from '@/auth/useRoleFlags';
import { annotateSubstituteEligibility, buildNoticeRuleInfo } from '@/utils/leaveUtils';
import { formatLocalDate, getTurkishHolidaysForLeaveDeduction } from '@/utils/dateUtils';
import DeleteNameConfirm, { deleteNameMatches } from '@/components/DeleteNameConfirm';
import ImageLightbox from '@/components/ImageLightbox';

const getApproverRoleLabel = (role) => {
  const r = (role || '').trim();
  switch (r) {
    case 'formen':
      return 'FORMEN';
    case 'bolge_muhendisi':
      return 'BM';
    case 'mudur_yardimcisi':
      return 'MY';
    case 'mudur':
      return 'MÜDÜR';
    default:
      return r ? r.toUpperCase() : '';
  }
};

// Resmî tatil listesi PROJENİN ORTAK kaynağından (utils/dateUtils) gelir.
// Kartın içinde ayrı bir kopya duruyordu; tablo bir yerde güncellenip diğeri
// unutulduğunda sessizce çelişen gün sayısı üretirdi. Tek kaynağa bağlandı.
const ALL_DEDUCTION_HOLIDAYS = new Set(
  [2024, 2025, 2026].flatMap((year) => getTurkishHolidaysForLeaveDeduction(year))
);

/**
 * İzin aralığında BAKİYEDEN DÜŞEN günler (Date listesi).
 * Hafta sonu ve resmî tatiller hariçtir. Devlet memurunda mevzuat gereği tüm
 * günler düştüğü için aralığın tamamı döner — İzin Özeti'ndeki sayımla aynı kural.
 */
const getLeaveChargeableDays = (startDate, endDate, isCivilServant = false) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];

  const days = [];
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = ALL_DEDUCTION_HOLIDAYS.has(formatLocalDate(current));
    if (isCivilServant || (!isWeekend && !isHoliday)) {
      days.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const LeaveRequestCard = ({
  request, 
  personnelId, 
  userRole,
  filterApprovalHistory,
  showPersonnelName = false,
  showCancelButton = false,
  showActionButtons = false,
  showDeleteButton = false,
  showMudurSocialReportNote = false,
  mudurSocialReportOverrideEnabled = false,
  isReadOnly = false,
  onActionClick,
  onUpdate,
  // Verilirse personel adı tıklanabilir olur (personel detay diyalogunu açar).
  onPersonnelClick = null,
  personnel = [],
  parks = [],
  regions = []
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [substituteOpen, setSubstituteOpen] = useState(false);
  const [updatingSubstitute, setUpdatingSubstitute] = useState(false);
  const [showParkSubstituteDialog, setShowParkSubstituteDialog] = useState(false);
  const [showSimpleSubDialog, setShowSimpleSubDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  // Silme üçüncü onayı: personel adı birebir yazılmadan "Evet, Sil" açılmaz.
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // Rapor fotoğrafı tam ekran görüntüleyicisi (yakınlaştır/döndür).
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [locallyDeleted, setLocallyDeleted] = useState(false);
  const [simpleSubId, setSimpleSubId] = useState(request.substitute_personnel_id || "");
  const [simpleReason, setSimpleReason] = useState("");
  const [simpleDirty, setSimpleDirty] = useState(false);
  const [parkSubstitutes, setParkSubstitutes] = useState([]);
  const [localSavedParkSubstitutes, setLocalSavedParkSubstitutes] = useState(null);
  const [localSavedParkHistory, setLocalSavedParkHistory] = useState(null);
  const [substituteEligibilityMap, setSubstituteEligibilityMap] = useState({});
  const [substituteEligibilityLoading, setSubstituteEligibilityLoading] = useState(false);
  // Uygunluk verisi YÜKLENEMEDİYSE (fetch hata/timeout) fail-CLOSED davran: personel
  // durumu (status) hiçbir zaman 'on_leave' olmadığından UI tamamen bu map'e bağlı;
  // boş map'e sessizce düşmek "herkes uygun" (fail-open) demek olur ki güvenli değil.
  const [substituteEligibilityError, setSubstituteEligibilityError] = useState(false);
  const canBypassReadOnly =
    String(request?.current_approver_id || "") === String(personnelId || "") ||
    String(request?.current_approver_delegated_from_id || "") === String(personnelId || "");
  const editBlockedByReadOnly = isReadOnly && !canBypassReadOnly;
  
  const currentApproverPerson = useMemo(() => {
    if (!Array.isArray(personnel) || personnel.length === 0) return null;
    if (request?.current_approver_id) {
      return personnel.find(p => p.id === request.current_approver_id) || null;
    }

    // Fallback: approval_history + approval_chain üzerinden sıradaki kişiyi bul
    const chain = request?.approval_chain || [];
    if (!Array.isArray(chain) || chain.length === 0) return null;
    const history = request?.approval_history || [];
    const lastApproverId = history.length > 0 ? history[history.length - 1]?.approver_id : null;
    if (lastApproverId && chain.includes(lastApproverId)) {
      const idx = chain.indexOf(lastApproverId);
      const nextId = chain[idx + 1];
      if (nextId) {
        return personnel.find(p => p.id === nextId) || null;
      }
    }
    return personnel.find(p => p.id === chain[0]) || null;
  }, [request?.current_approver_id, request?.approval_chain, request?.approval_history, personnel]);

  // İncelemede olan kişinin ad/rolü: önce client-side personel listesi, yoksa
  // backend'in denormalize ettiği alanlar. Böylece talep sahibi (ör. park görevlisi)
  // kendi listesinde MY/Müdür bulunmasa da "İncelemede: MY ..." görebilir.
  const currentApproverDisplayName =
    currentApproverPerson?.name || request?.current_approver_name || "";
  const currentApproverDisplayRole =
    currentApproverPerson?.role || request?.current_approver_role || "";

  // Özel değerlendirme / geçmiş gün bilgisi (yönetici kartında görünür kılmak için).
  const reviewModeInfo = useMemo(() => {
    const mode = request?.review_mode;
    if (!mode || mode === "normal") return null;
    const codes = Array.isArray(request?.review_reason_codes) ? request.review_reason_codes : [];
    // Rozet etiketi + kural açıklaması ORTAK yardımcıdan gelir (leaveUtils), çünkü
    // aynı metni karar listesindeki özet satır da gösteriyor. İki yerde ayrı kurmak
    // er geç birbiriyle çelişen iki gösterim üretir.
    const noticeInfo = buildNoticeRuleInfo(request);
    const ruleLabel = noticeInfo?.label || null;
    const ruleNote = noticeInfo?.note || null;
    const labels = {
      special_review: "Özel Değerlendirme",
      exception_review: "İstisna Gerektiren Acil",
      auto_reject: "Kural Dışı",
    };
    const classes = {
      special_review: "bg-blue-50 text-blue-700 border-blue-300",
      exception_review: "bg-amber-50 text-amber-700 border-amber-300",
      auto_reject: "bg-red-50 text-red-700 border-red-300",
    };
    return {
      label: labels[mode] || mode,
      className: classes[mode] || "bg-gray-50 text-gray-700 border-gray-300",
      ruleLabel,
      ruleNote,
      // Vekalet çakışması: bu talep, sahibinin AKTİF vekalet göreviyle çakışıyor.
      // Onaycı açıkça görsün (onaylanırsa vekaletin yeniden atanması gerekir).
      substitutionConflict: codes.includes("ACTIVE_SUBSTITUTION_CONFLICT"),
    };
  }, [request?.review_mode, request?.review_reason_codes, request?.start_date]);

  // Kural açıklaması notu tıklayınca açılır (fareyle gelince title olarak da çıkar).
  const [ruleNoteOpen, setRuleNoteOpen] = useState(false);

  // "Bilgi amaçlı gözlem": özel/istisna değerlendirme doğrudan MY'ye gittiğinden,
  // bypass edilen Formen/BM talebi salt-görüntüleme olarak görür (karar veremez).
  // Görüntüleyen ne talep sahibi ne de sıradaki onaycı ise gözlemcidir.
  // Karar verilmiş talepte "karar şurada" demek tanımı gereği yanlıştır: onaylanınca
  // sıradaki onaycı kalmadığı için eski koşul talep sahibi dışında HERKESE rozeti
  // gösteriyor, üstelik rol boş kaldığından sabit "MY" yazıyordu (Müdür kendi
  // onayladığı talepte bile "karar MY'da" görüyordu). İki koşul eklendi:
  //   - talep hâlâ karar bekliyor olmalı,
  //   - sıradaki onaycının rolü GERÇEKTEN biliniyor olmalı (uydurma varsayılan yok).
  const isDecidedRequest = ["approved", "rejected", "cancelled"].includes(request?.status);
  const isObserverView =
    !!reviewModeInfo &&
    !!personnelId &&
    !isDecidedRequest &&
    Boolean(currentApproverDisplayRole) &&
    String(request?.current_approver_id || "") !== String(personnelId) &&
    String(request?.personnel_id || "") !== String(personnelId);

  const currentDelegationPlan = useMemo(() => {
    const stageRoleKey = getApproverRoleLabel(userRole);
    const draftPlan = request?.delegate_plan_drafts?.[stageRoleKey]?.plan;
    const normalizedDraftPlan = Array.isArray(draftPlan)
      ? {
          mode: "PER_PARK",
          singleDelegateUserId: null,
          perPark: draftPlan.map((x) => ({
            park_id: x?.parkId,
            park_name: x?.parkName,
            delegate_id: x?.delegateId,
            delegate_name: x?.delegateName,
            change_reason: x?.reason || "",
          }))
        }
      : null;
    const plan = normalizedDraftPlan || ((draftPlan && typeof draftPlan === "object") ? draftPlan : request?.delegationPlan);
    if (plan && typeof plan === "object") {
      return {
        mode: String(plan.mode || "").toUpperCase() === "PER_PARK" ? "PER_PARK" : "SINGLE",
        singleDelegateUserId: plan.singleDelegateUserId || null,
        perPark: Array.isArray(plan.perPark) ? plan.perPark : []
      };
    }
    const legacyPerPark = Array.isArray(request?.park_substitutes) ? request.park_substitutes : [];
    if (legacyPerPark.length > 0) {
      return {
        mode: "PER_PARK",
        singleDelegateUserId: null,
        perPark: legacyPerPark.map((ps) => ({
          park_id: ps?.park_id,
          park_name: ps?.park_name,
          delegate_id: ps?.delegate_id || ps?.substitute_id || null,
          delegate_name: ps?.delegate_name || ps?.substitute_name || null,
          change_reason: ps?.change_reason || ""
        }))
      };
    }
    const requestedPerPark = Array.isArray(request?.requestedParkSubstitutes) ? request.requestedParkSubstitutes : [];
    if (requestedPerPark.length > 0) {
      return {
        mode: "PER_PARK",
        singleDelegateUserId: null,
        perPark: requestedPerPark.map((ps) => ({
          park_id: ps?.park_id,
          park_name: ps?.park_name,
          delegate_id: ps?.delegate_id || ps?.substitute_id || null,
          delegate_name: ps?.delegate_name || ps?.substitute_name || null,
          change_reason: ps?.change_reason || ps?.reason || ""
        }))
      };
    }
    return {
      mode: "SINGLE",
      singleDelegateUserId: request?.substitute_personnel_id || null,
      perPark: []
    };
  }, [request?.delegate_plan_drafts, request?.delegationPlan, request?.park_substitutes, request?.requestedParkSubstitutes, request?.substitute_personnel_id, userRole]);

  const effectiveParkSubstitutes = useMemo(() => {
    // Park listeleri her yerde alfabetik (TR) sıralı görünsün — kullanıcı "Mevcut
    // vekil planı" ile "Vekil Düzenle"/park listesinin aynı sırada olmasını bekliyor.
    const _sorted = (arr) =>
      Array.isArray(arr)
        ? [...arr].sort((a, b) =>
            String(a?.park_name || "").localeCompare(String(b?.park_name || ""), "tr")
          )
        : [];
    if (Array.isArray(localSavedParkSubstitutes)) return _sorted(localSavedParkSubstitutes);
    if (Array.isArray(currentDelegationPlan?.perPark) && currentDelegationPlan.perPark.length > 0) {
      return _sorted(currentDelegationPlan.perPark.map((ps) => ({
        park_id: ps?.park_id,
        park_name: ps?.park_name,
        substitute_id: ps?.delegate_id || ps?.substitute_id || null,
        substitute_name: ps?.delegate_name || ps?.substitute_name || null,
        change_reason: ps?.change_reason || "",
        changed_by_id: ps?.changed_by_id || null,
        changed_by_name: ps?.changed_by_name || null
      })));
    }
    if (Array.isArray(request?.park_substitutes) && request.park_substitutes.length > 0) {
      return _sorted(request.park_substitutes);
    }
    const requestedPerPark = Array.isArray(request?.requestedParkSubstitutes) ? request.requestedParkSubstitutes : [];
    if (requestedPerPark.length > 0) {
      return _sorted(requestedPerPark.map((ps) => ({
        park_id: ps?.park_id,
        park_name: ps?.park_name,
        substitute_id: ps?.delegate_id || ps?.substitute_id || null,
        substitute_name: ps?.delegate_name || ps?.substitute_name || null,
        change_reason: ps?.change_reason || ps?.reason || "",
        changed_by_id: null,
        changed_by_name: null
      })));
    }
    return [];
  }, [localSavedParkSubstitutes, currentDelegationPlan, request?.park_substitutes, request?.requestedParkSubstitutes]);

  const currentPlanSingleDelegateId = currentDelegationPlan?.singleDelegateUserId || null;
  const currentPlanSingleDelegateName = useMemo(() => {
    if (!currentPlanSingleDelegateId) return "";
    const found = (personnel || []).find((p) => String(p?.id || "") === String(currentPlanSingleDelegateId));
    return found?.name || request?.substitute_personnel_name || "";
  }, [currentPlanSingleDelegateId, personnel, request?.substitute_personnel_name]);

  const requestedSingleDelegateId = request?.requestedSubstituteUserId || null;
  const requestedSingleDelegateName = useMemo(() => {
    if (!requestedSingleDelegateId) return "";
    const found = (personnel || []).find((p) => String(p?.id || "") === String(requestedSingleDelegateId));
    // Backend'in denormalize ettiği ad (requestedSubstituteName) fallback: kişi
    // görüntüleyenin rol-kapsamlı personel listesinde olmasa da isim gösterilir,
    // UUID sızıntısı önlenir.
    return found?.name || request?.requestedSubstituteName || "";
  }, [requestedSingleDelegateId, personnel, request?.requestedSubstituteName]);

  const requestedParkDelegateList = useMemo(() => {
    const list = Array.isArray(request?.requestedParkSubstitutes) ? request.requestedParkSubstitutes : [];
    return list.map((ps) => {
      const delegateId = ps?.delegate_id || ps?.substitute_id || null;
      const found = delegateId ? (personnel || []).find((p) => String(p?.id || "") === String(delegateId)) : null;
      return {
        park_id: ps?.park_id,
        park_name: ps?.park_name || "Park",
        delegate_id: delegateId,
        delegate_name: ps?.delegate_name || ps?.substitute_name || found?.name || "Seçilmedi",
        change_reason: ps?.change_reason || ps?.reason || ""
      };
    });
  }, [request?.requestedParkSubstitutes, personnel]);

  const requesterRoleLabel = useMemo(() => {
    return getApproverRoleLabel(request?.personnel_role) || "Personel";
  }, [request?.personnel_role]);

  // İzin sahibinin parkları
  const personnelParks = useMemo(() => {
    const assignedFromParks = (parks || []).filter(p => 
      p.assigned_personnel_ids && 
      p.assigned_personnel_ids.includes(request.personnel_id)
    );
    if (assignedFromParks.length > 0) return assignedFromParks;

    // Park atamaları leave sırasında değişmiş olabilir; talep içindeki parkları fallback kullan.
    const fallbackById = new Map();
    for (const ps of (effectiveParkSubstitutes || [])) {
      const pid = ps?.park_id;
      if (!pid) continue;
      const parkObj = (parks || []).find(p => p.id === pid);
      fallbackById.set(pid, parkObj || { id: pid, name: ps?.park_name || "Park" });
    }
    return Array.from(fallbackById.values());
  }, [request.personnel_id, parks, effectiveParkSubstitutes]);

  const parkIdNameMap = useMemo(() => {
    const map = new Map();
    (personnelParks || []).forEach((p) => {
      if (p?.id) map.set(String(p.id), p.name || "");
    });
    (effectiveParkSubstitutes || []).forEach((ps) => {
      if (ps?.park_id) map.set(String(ps.park_id), ps.park_name || map.get(String(ps.park_id)) || "");
    });
    (requestedParkDelegateList || []).forEach((ps) => {
      if (ps?.park_id) map.set(String(ps.park_id), ps.park_name || map.get(String(ps.park_id)) || "");
    });
    return map;
  }, [personnelParks, effectiveParkSubstitutes, requestedParkDelegateList]);

  // İzin sahibinin formen_id'si (aynı formene bağlı personelleri filtrelemek için)
  const leavePersonnel = useMemo(() => {
    return personnel.find(p => p.id === request.personnel_id);
  }, [personnel, request.personnel_id]);

  const normalizeRole = (role) => (role || "").toLowerCase().replace("ç", "c").replace("ı", "i").replace("ş", "s").replace("ğ", "g").replace("ü", "u").replace("ö", "o").replace(/\s+/g, "_");
  const canViewDelegateReasonDetails = ["admin", "formen", "bolge_muhendisi", "mudur_yardimcisi", "mudur"].includes(normalizeRole(userRole));
  const normalizedUserRole = normalizeRole(userRole);
  const isSocialOrReport = request?.reason_type === "Sosyal İzin" || request?.reason_type === "Rapor";
  const canDeleteRequest = normalizedUserRole === "admin" || normalizedUserRole === "mudur" || (normalizedUserRole === "mudur_yardimcisi" && isSocialOrReport);

  // Vekil olabilecek personeller (rol ve hiyerarşiye göre)
  const availableSubstitutes = useMemo(() => {
    if (!personnel.length || !leavePersonnel) return [];
    const leaveRole = normalizeRole(leavePersonnel.role);
    const isActive = (p) => !['passive', 'inactive', 'deleted'].includes(String(p?.status || '').toLowerCase());

    // Park görevlisi / çim ekibi -> aynı formen altındaki park görevlileri
    if (leaveRole === 'park_gorevlisi' || leaveRole === 'cim_ekibi') {
      return personnel.filter(p =>
        p.id !== request.personnel_id &&
        isActive(p) &&
        (normalizeRole(p.role) === 'park_gorevlisi' || normalizeRole(p.role) === 'cim_ekibi') &&
        p.formen_id === leavePersonnel.formen_id &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }

    // Formen -> aynı MY'ye bağlı diğer formenler
    if (leaveRole === 'formen') {
      return personnel.filter(p =>
        p.id !== request.personnel_id &&
        isActive(p) &&
        normalizeRole(p.role) === 'formen' &&
        p.mudur_yardimcisi_id === leavePersonnel.mudur_yardimcisi_id &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }

    // BM -> diğer BM'ler
    if (leaveRole === 'bolge_muhendisi') {
      return personnel.filter(p =>
        p.id !== request.personnel_id &&
        isActive(p) &&
        normalizeRole(p.role) === 'bolge_muhendisi' &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }

    // MY -> diğer MY'lar
    if (leaveRole === 'mudur_yardimcisi') {
      return personnel.filter(p =>
        p.id !== request.personnel_id &&
        isActive(p) &&
        normalizeRole(p.role) === 'mudur_yardimcisi' &&
        p.name
      ).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    }

    return [];
  }, [personnel, request.personnel_id, leavePersonnel]);

  // ORTAK helper (leaveUtils) — create ekranıyla BİREBİR aynı uygunluk hesabı:
  // tarih-aralığı çakışması (map) VEYA personelin o an izinli/raporlu (status)
  // olması. Eskiden edit yalnız map'e bakıyordu → fetch boş/başarısız olunca ya da
  // kişi o an izindeyken izinli personel "uygun" görünüyordu (bu bug'ın kök nedeni).
  const availableSubstitutesWithEligibility = useMemo(() => {
    const annotated = annotateSubstituteEligibility(availableSubstitutes, substituteEligibilityMap);
    if (substituteEligibilityError) {
      // FAIL-CLOSED: uygunluk verisi yüklenemedi → hiçbir aday seçilemesin
      // (aksi halde izinli personel "uygun" görünür — fail-open güvensiz).
      return annotated.map((p) => ({
        ...p,
        isUnavailableForRange: true,
        unavailableText: p.unavailableText || "uygunluk bilgisi yüklenemedi",
      }));
    }
    return annotated;
  }, [availableSubstitutes, substituteEligibilityMap, substituteEligibilityError]);

  const loadSubstituteEligibility = async () => {
    if (!request?.id || availableSubstitutes.length === 0) {
      setSubstituteEligibilityMap({});
      setSubstituteEligibilityError(false);
      return;
    }
    try {
      setSubstituteEligibilityLoading(true);
      const idsCsv = availableSubstitutes.map((p) => p.id).filter(Boolean).join(",");
      // CREATE ekranıyla AYNI (çalıştığı kanıtlı) uç: /substitute-eligibility-preview.
      // Eskiden request-based /leave-requests/{id}/substitute-eligibility kullanılıyordu;
      // canlıda (üst amir oturumu) başarısız olup fail-open'a düşüyordu. Preview
      // STATELESS'tir (talep-görünürlük kapısı yok), aynı _eligibility_items mantığını
      // talebin start/end tarihleriyle çalıştırır → izinli aday burada da yakalanır.
      const resp = await apiClient.get("/substitute-eligibility-preview", {
        timeout: 8000,
        params: {
          start_date: String(request.start_date || "").slice(0, 10),
          end_date: String(request.end_date || request.return_date || "").slice(0, 10),
          candidate_ids: idsCsv,
          exclude_personnel_id: request.personnel_id || undefined,
        },
      });
      const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
      const nextMap = {};
      items.forEach((it) => {
        const pid = String(it?.personnel_id || "");
        if (!pid) return;
        nextMap[pid] = it;
      });
      setSubstituteEligibilityMap(nextMap);
      setSubstituteEligibilityError(false);
    } catch {
      // FAIL-CLOSED: sessizce boş map'e düşme; hata bayrağı ile tüm adayları kilitle.
      setSubstituteEligibilityMap({});
      setSubstituteEligibilityError(true);
    } finally {
      setSubstituteEligibilityLoading(false);
    }
  };

  // Formen/BM/MY için sorumlu bölgeler
  const personnelRegions = useMemo(() => {
    if (!leavePersonnel || !regions.length) return [];
    const leaveRole = normalizeRole(leavePersonnel.role);
    if (leaveRole === 'formen') {
      return regions.filter(r =>
        (r.assigned_formen_ids || []).includes(leavePersonnel.id) ||
        (leavePersonnel.formen_region_id && r.id === leavePersonnel.formen_region_id)
      );
    }
    if (leaveRole === 'bolge_muhendisi') {
      return regions.filter(r => r.assigned_bolge_muhendisi_id === leavePersonnel.id);
    }
    if (leaveRole === 'mudur_yardimcisi') {
      const bms = personnel.filter(p => normalizeRole(p.role) === 'bolge_muhendisi' && p.mudur_yardimcisi_id === leavePersonnel.id);
      const bmIds = new Set(bms.map(b => b.id));
      return regions.filter(r => bmIds.has(r.assigned_bolge_muhendisi_id));
    }
    return [];
  }, [leavePersonnel, regions, personnel]);

  // Seçili vekil (tek vekil modunda)
  const currentSubstitute = useMemo(() => {
    if (!currentPlanSingleDelegateId) return null;
    return personnel.find(p => p.id === currentPlanSingleDelegateId);
  }, [currentPlanSingleDelegateId, personnel]);

  const parkSubstituteHistoryEntries = useMemo(() => {
    const source = Array.isArray(localSavedParkHistory)
      ? localSavedParkHistory
      : request.park_substitute_history;
    const list = Array.isArray(source)
      ? source
      : [];
    // Güvenli UI: Her park için sadece en son değişiklik göster
    const latestByPark = new Map();
    [...list]
      .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
      .forEach((entry) => {
        const key = String(entry?.park_id || "");
        if (!key || latestByPark.has(key)) return;
        latestByPark.set(key, entry);
      });
    return Array.from(latestByPark.values());
  }, [request.park_substitute_history, localSavedParkHistory]);

  const delegatePlanHistoryEntries = useMemo(() => {
    const finalizedList = Array.isArray(request?.delegate_plan_history) ? request.delegate_plan_history : [];
    const draftsObj = (request?.delegate_plan_drafts && typeof request.delegate_plan_drafts === "object")
      ? request.delegate_plan_drafts
      : {};

    const draftList = Object.values(draftsObj)
      .filter((entry) => entry && typeof entry === "object")
      .map((entry) => ({
        stageRole: entry.stageRole,
        changedById: entry.updatedById || entry.changedById || entry.performedByUserId || null,
        changedByName: entry.updatedByName || entry.changedByName || entry.performedByName || null,
        performedByUserId: entry.performedByUserId || entry.updatedById || entry.changedById || null,
        performedByName: entry.performedByName || entry.updatedByName || entry.changedByName || null,
        onBehalfOfUserId: entry.onBehalfOfUserId || null,
        onBehalfOfName: entry.onBehalfOfName || null,
        ts: entry.updatedAt || entry.ts || null,
        changes: Array.isArray(entry.changeSummary) ? entry.changeSummary : (Array.isArray(entry.changes) ? entry.changes : []),
        changeSummary: Array.isArray(entry.changeSummary) ? entry.changeSummary : (Array.isArray(entry.changes) ? entry.changes : []),
        noChange: entry.noChange === true || (!Array.isArray(entry.changeSummary) && !Array.isArray(entry.changes)),
        isDraft: true
      }));

    const merged = [...finalizedList, ...draftList].sort((a, b) => new Date(a?.ts || 0) - new Date(b?.ts || 0));

    // Aynı stage+zaman kombinasyonu hem draft hem finalize olarak geldiyse finalize kaydı koru
    const dedupMap = new Map();
    merged.forEach((entry) => {
      const key = `${String(entry?.stageRole || "").trim()}__${String(entry?.ts || "").trim()}__${String(entry?.changedById || "").trim()}`;
      const prev = dedupMap.get(key);
      if (!prev) {
        dedupMap.set(key, entry);
        return;
      }
      if (prev?.isDraft && !entry?.isDraft) {
        dedupMap.set(key, entry);
      }
    });

    return Array.from(dedupMap.values()).sort((a, b) => new Date(a?.ts || 0) - new Date(b?.ts || 0));
  }, [request?.delegate_plan_history, request?.delegate_plan_drafts]);

  // Park → o parkı EN SON değiştiren kişi (mevcut park_substitutes.changed_by_name
  // boş kalmış eski kayıtlar için geçmişten fallback). Liste eskiden-yeniye sıralı
  // olduğundan son atama en güncel düzenleyendir.
  const changedByByPark = useMemo(() => {
    const map = {};
    for (const entry of (delegatePlanHistoryEntries || [])) {
      const changes = Array.isArray(entry?.changes)
        ? entry.changes
        : (Array.isArray(entry?.changeSummary) ? entry.changeSummary : []);
      const who = entry?.changedByName || entry?.performedByName || null;
      if (!who) continue;
      for (const chg of changes) {
        const pid = String(chg?.parkId || "");
        if (pid) map[pid] = who;
      }
    }
    return map;
  }, [delegatePlanHistoryEntries]);

  const normalizeDisplayedSubstituteName = (rawName) => {
    const text = (rawName || "").trim();
    if (!text) return "";
    const lowered = text.toLocaleLowerCase("tr-TR");
    if (lowered === "seçilmedi" || lowered === "secilmedi") return "";
    return text;
  };

  const inferredDefaultSubstituteName = useMemo(() => {
    const withoutBefore = (parkSubstituteHistoryEntries || [])
      .filter((h) => !normalizeDisplayedSubstituteName(h?.before?.name))
      .map((h) => ({
        name: normalizeDisplayedSubstituteName(h?.after?.name),
        hasReason: Boolean((h?.reason || "").trim())
      }))
      .filter((x) => Boolean(x.name));
    if (withoutBefore.length === 0) return "";
    const noReasonCandidate = withoutBefore.find((x) => !x.hasReason);
    return noReasonCandidate?.name || withoutBefore[0].name || "";
  }, [parkSubstituteHistoryEntries]);

  const actorInitialBeforeFallbackMap = useMemo(() => {
    const map = new Map();
    for (const h of parkSubstituteHistoryEntries || []) {
      const actorId = String(h?.actor_id || "");
      if (!actorId || map.has(actorId)) continue;
      const beforeName = normalizeDisplayedSubstituteName(h?.before?.name);
      const afterName = normalizeDisplayedSubstituteName(h?.after?.name);
      if (!beforeName && afterName) {
        map.set(actorId, afterName);
      }
    }
    return map;
  }, [parkSubstituteHistoryEntries]);

  const resolveHistoryTransitionNames = (entry) => {
    const requestedDefault = normalizeDisplayedSubstituteName(request.substitute_personnel_name) || inferredDefaultSubstituteName;
    const actorFallback = actorInitialBeforeFallbackMap.get(String(entry?.actor_id || "")) || "";
    const beforeRaw = normalizeDisplayedSubstituteName(entry?.before?.name);
    const afterRaw = normalizeDisplayedSubstituteName(entry?.after?.name);
    const baselineBefore = actorFallback || requestedDefault;
    const beforeName = (beforeRaw && !(actorFallback && beforeRaw !== actorFallback))
      ? beforeRaw
      : (baselineBefore || beforeRaw || "Seçilmedi");
    const afterName = afterRaw || "Seçilmedi";
    return { beforeName, afterName };
  };

  React.useEffect(() => {
    setLocalSavedParkSubstitutes(null);
    setLocalSavedParkHistory(null);
    setLocallyDeleted(false);
  }, [request?.id]);

  // Park bazlı vekilleri initialize et
  const initializeParkSubstitutes = async () => {
    const existingParkSubs = effectiveParkSubstitutes;
    const initialized = personnelParks.map(park => {
      const existing = existingParkSubs.find(ps => ps.park_id === park.id);
      return {
        park_id: park.id,
        park_name: park.name,
        substitute_id: existing?.substitute_id || currentPlanSingleDelegateId || null,
        substitute_name: existing?.substitute_name || currentPlanSingleDelegateName || null,
        change_reason: existing?.change_reason || "",
        // Orijinal değerleri sakla - değişip değişmediğini anlamak için
        original_substitute_id: existing?.substitute_id || currentPlanSingleDelegateId || null,
        original_change_reason: existing?.change_reason || "",
        original_changed_by_name: existing?.changed_by_name || null,
        original_changed_by_id: existing?.changed_by_id || null
      };
    });
    setParkSubstitutes(initialized);
    await loadSubstituteEligibility();
    setShowParkSubstituteDialog(true);
  };

  // Park için vekil değiştir
  const handleParkSubstituteSelect = (parkId, substituteId) => {
    const sub = personnel.find(p => p.id === substituteId);
    setParkSubstitutes(prev => prev.map(ps => 
      ps.park_id === parkId
        ? { ...ps, substitute_id: substituteId, substitute_name: sub?.name || null, isChanged: true }
        : ps
    ));
  };

  // Park için sebep değiştir
  const handleParkReasonChange = (parkId, reason) => {
    setParkSubstitutes(prev => prev.map(ps => 
      ps.park_id === parkId
        ? { ...ps, change_reason: reason, isChanged: true }
        : ps
    ));
  };

  const logNetworkError = (err, method, url) => {
    console.error("[delegate-save][network]", {
      method,
      url,
      code: err?.code || null,
      message: err?.message || "unknown",
      hasResponse: Boolean(err?.response),
      online: typeof navigator !== "undefined" ? navigator.onLine : null
    });
  };

  // Park bazlı vekilleri kaydet - parkSubstitutes parametresi alabilir
  const saveParkSubstitutes = async (parksToSave = null) => {
    console.log("save clicked");
    if (editBlockedByReadOnly) {
      if (!isGolgeAdmin) toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    if (!request?.id) {
      toast.error("İzin talebi bulunamadı.");
      return;
    }
    // UX: kullanıcı tıklayınca modal anında kapansın, işlem arka planda sürsün.
    setShowParkSubstituteDialog(false);
    setUpdatingSubstitute(true);
    try {
      const currentUser = personnel.find(p => p.id === personnelId);
      const dataToSave = parksToSave || parkSubstitutes;
      
      // Her park için: değiştiyse yeni bilgiyi, değişmediyse eski bilgiyi gönder
      const finalParkSubstitutes = dataToSave.map(ps => {
        const hasChanged = ps.isChanged ||
          ps.substitute_id !== ps.original_substitute_id ||
          ps.change_reason !== ps.original_change_reason;
        return {
          park_id: ps.park_id || "",
          park_name: ps.park_name || "",
          substitute_id: ps.substitute_id ?? null,
          substitute_name: ps.substitute_name ?? null,
          change_reason: ps.change_reason || "",
          changed_by_id: hasChanged ? personnelId : (ps.original_changed_by_id ?? null),
          changed_by_name: hasChanged ? (currentUser?.name || "") : (ps.original_changed_by_name || "")
        };
      });

      const payload = {
        park_substitutes: finalParkSubstitutes,
        changed_by_id: personnelId || null,
        changed_by_name: (currentUser?.name || "") || ""
      };
      const getPersistedLeaveRequest = async () => {
        try {
          const resp = await apiClient.get("/leave-requests", { timeout: 6000 });
          const all = Array.isArray(resp?.data) ? resp.data : [];
          const fresh = all.find((lr) => String(lr?.id || "") === String(request?.id || ""));
          if (!fresh) return null;
          const freshSubs = Array.isArray(fresh?.park_substitutes) ? fresh.park_substitutes : [];
          const isMatched = finalParkSubstitutes.every((ps) => {
            const m = freshSubs.find((x) => String(x?.park_id || "") === String(ps?.park_id || ""));
            return (
              String(m?.substitute_id || "") === String(ps?.substitute_id || "") &&
              String(m?.change_reason || "") === String(ps?.change_reason || "")
            );
          });
          return isMatched ? fresh : null;
        } catch {
          return null;
        }
      };
      const opts = { timeout: 5000, headers: { "Content-Type": "application/json" } };
      let response;
      try {
        const postUrl = `/leave-requests/${request.id}/park-substitutes`;
        console.log("[delegate-save] request", { method: "POST", url: postUrl });
        try {
          response = await apiClient.post(postUrl, payload, opts);
        } catch (postErr) {
          logNetworkError(postErr, "POST", postUrl);
        }
        if (!response) {
          throw new Error("POST isteği başarısız");
        }
      } catch (postError) {
        const persisted = await getPersistedLeaveRequest();
        if (persisted) {
          response = {
            data: {
              park_substitutes: Array.isArray(persisted.park_substitutes) ? persisted.park_substitutes : finalParkSubstitutes,
              park_substitute_history: Array.isArray(persisted.park_substitute_history) ? persisted.park_substitute_history : []
            }
          };
        }
        if (!response) {
          const putUrl = `/leave-requests/${request.id}/park-substitutes`;
          console.log("[delegate-save] request", { method: "PUT", url: putUrl });
          try {
            response = await apiClient.put(putUrl, payload, opts);
          } catch (putErr) {
            logNetworkError(putErr, "PUT", putUrl);
          }
        }
        if (!response) {
          const persistedAlt = await getPersistedLeaveRequest();
          if (persistedAlt) {
            response = {
              data: {
                park_substitutes: Array.isArray(persistedAlt.park_substitutes) ? persistedAlt.park_substitutes : finalParkSubstitutes,
                park_substitute_history: Array.isArray(persistedAlt.park_substitute_history) ? persistedAlt.park_substitute_history : []
              }
            };
          }
        }
        if (!response) throw postError;
      }

      const responseStatus = Number(response?.status || 200);
      console.log("delegate save status:", responseStatus, response?.data);
      if (responseStatus < 200 || responseStatus >= 300) {
        throw new Error(`Kaydetme başarısız (HTTP ${responseStatus})`);
      }
      if (Array.isArray(response?.data?.park_substitutes)) {
        setLocalSavedParkSubstitutes(response.data.park_substitutes);
      }
      if (Array.isArray(response?.data?.park_substitute_history)) {
        setLocalSavedParkHistory(response.data.park_substitute_history);
      }

      setShowParkSubstituteDialog(false);
      toast.success("Park vekilleri güncellendi");

      // Kaydet başarılıysa listeyi güncelle.
      if (onUpdate) {
        try {
          await Promise.resolve(onUpdate());
        } catch (refreshErr) {
          console.warn("Leave requests refresh failed after park substitute update:", refreshErr);
        }
      }
    } catch (error) {
      console.error("Park vekilleri güncellenemedi:", error);
      const statusCode = error?.response?.status;
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : error.message || "Park vekilleri güncellenemedi";
      toast.error(statusCode ? `Kaydetme hatası (${statusCode}): ${msg}` : msg);
      return;
    } finally {
      setUpdatingSubstitute(false);
    }
  };

  // Tek vekil değiştirme fonksiyonu (geriye uyumluluk için) – önce POST, timeout/CORS için
  const handleSubstituteChange = async (newSubstituteId, reason = "") => {
    console.log("save clicked");
    if (editBlockedByReadOnly) {
      if (!isGolgeAdmin) toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    setUpdatingSubstitute(true);
    try {
      const currentUser = personnel.find(p => p.id === personnelId);
      const payload = {
        substitute_personnel_id: newSubstituteId,
        changed_by_id: personnelId,
        changed_by_name: currentUser?.name || '',
        change_reason: reason || ''
      };
      const timeoutMs = 60000;
      let response;
      const postUrl = `/leave-requests/${request.id}/substitute`;
      console.log("[delegate-save] request", { method: "POST", url: postUrl });
      try {
        response = await apiClient.post(postUrl, payload, { timeout: timeoutMs });
      } catch (postErr) {
        logNetworkError(postErr, "POST", postUrl);
      }
      if (!response) {
        const putUrl = `/leave-requests/${request.id}/substitute`;
        console.log("[delegate-save] request", { method: "PUT", url: putUrl });
        try {
          response = await apiClient.put(putUrl, payload, { timeout: timeoutMs });
        } catch (putErr) {
          logNetworkError(putErr, "PUT", putUrl);
        }
      }
      if (!response) {
        throw new Error("Vekil kaydetme isteği başarısız");
      }
      const responseStatus = Number(response?.status || 200);
      console.log("single delegate save status:", responseStatus, response?.data);
      if (responseStatus < 200 || responseStatus >= 300) {
        throw new Error(`Kaydetme başarısız (HTTP ${responseStatus})`);
      }
      toast.success(newSubstituteId ? "Vekil güncellendi" : "Vekil kaldırıldı");
      if (onUpdate) await Promise.resolve(onUpdate());
      setShowSimpleSubDialog(false);
      setSimpleDirty(false);
    } catch (error) {
      console.error("Vekil güncellenemedi:", error);
      const statusCode = error?.response?.status;
      const msg = error.response?.data?.detail || error.message || "Vekil güncellenemedi";
      toast.error(statusCode ? `Kaydetme hatası (${statusCode}): ${msg}` : msg);
    } finally {
      setUpdatingSubstitute(false);
      setSubstituteOpen(false);
    }
  };
  
  const filteredHistory = filterApprovalHistory
    ? filterApprovalHistory(request.approval_history, userRole, request.status, personnelId, request.personnel_id)
    : [];

  const handleCancel = async () => {
    if (isReadOnly) {
      if (!isGolgeAdmin) toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    if (window.confirm('İzin talebinizi iptal etmek istediğinizden emin misiniz?')) {
      try {
        await apiClient.post(`/leave-requests/${request.id}/cancel?personnel_id=${personnelId}`);
        toast.success("İzin talebi iptal edildi");
        if (onUpdate) onUpdate();
      } catch (error) {
        toast.error(error.response?.data?.detail || "İptal edilirken hata oluştu");
      }
    }
  };

  const handleDelete = async () => {
    const deleteBlockedByReadOnly = isReadOnly && !canDeleteRequest;
    if (deleteBlockedByReadOnly) {
      if (!isGolgeAdmin) toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    if (!canDeleteRequest) {
      toast.error("Bu izin türünü silme yetkiniz yok.");
      return;
    }
    console.log("DELETE leave request", { id: request.id });
    const toastId = toast.success("Silme isteği gönderildi");
    // UX: kullanıcı beklemesin, kartı hemen gizle. Hata olursa geri açılır.
    setLocallyDeleted(true);
    try {
      let response;
      const actorParam = `actor_id=${encodeURIComponent(personnelId || "")}`;
      const isRequestDeleted = async () => {
        try {
          const listResp = await apiClient.get("/leave-requests", { timeout: 8000 });
          const list = Array.isArray(listResp?.data) ? listResp.data : [];
          return !list.some((lr) => String(lr?.id || "") === String(request.id || ""));
        } catch {
          return false;
        }
      };
      let deleteError = null;
      try {
        response = await apiClient.post(
          `/leave-requests/${request.id}/delete?${actorParam}`,
          null,
          { timeout: 5000 }
        );
      } catch (postError) {
        deleteError = postError;
        if (await isRequestDeleted()) {
          response = { data: { deleted: true, message: "İzin talebi silindi" } };
        }
      }
      if (!response) throw deleteError || new Error("Silme isteği gönderilemedi");
      if (response?.data?.deleted === false) {
        toast.error(response?.data?.message || "İzin talebi silinemedi", { id: toastId });
        return;
      }
      setLocallyDeleted(true);
      toast.success("İzin talebi silindi", { id: toastId });
    } catch (error) {
      // Gerçekten silinemediyse kartı geri göster.
      setLocallyDeleted(false);
      console.error("Silme hatası:", error);
      if (error.response?.status === 404) {
        setLocallyDeleted(true);
        toast.error("İzin talebi zaten silinmiş. Liste yenilendi.", { id: toastId });
        return;
      }
      toast.error(error.response?.data?.detail || error.message || "Silinirken hata oluştu", { id: toastId });
    } finally {
      if (onUpdate) {
        try {
          await Promise.resolve(onUpdate());
        } catch (refreshErr) {
          console.warn("Silme sonrası liste yenileme hatası:", refreshErr);
        }
      }
    }
  };

  const handleDeleteWithConfirm = async () => {
    const deleteBlockedByReadOnly = isReadOnly && !canDeleteRequest;
    if (deleteBlockedByReadOnly) {
      if (!isGolgeAdmin) toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    if (!canDeleteRequest) {
      toast.error("Bu izin türünü silme yetkiniz yok.");
      return;
    }
    setShowDeleteConfirmDialog(true);
  };

  const handleDeleteConfirmSubmit = async () => {
    if (deleteSubmitting) return;
    // Üçüncü onay: personel adı birebir yazılmadan istek gitmez.
    if (!deleteNameMatches(deleteConfirmText, request.personnel_name)) return;
    setDeleteSubmitting(true);
    // Dialog'u anında kapat: kullanıcı "takıldı" hissi yaşamaz.
    setShowDeleteConfirmDialog(false);
    setDeleteConfirmText('');
    try {
      await handleDelete();
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const pid = personnelId != null ? String(personnelId) : "";
  const hasAlreadyActed = request.approval_history &&
    request.approval_history.some(step => String(step.approver_id) === pid);
  const isMudurSocialReport = isMudur && (request.reason_type === 'Sosyal İzin' || request.reason_type === 'Rapor');
  const canMudurOverride = isMudurSocialReport && mudurSocialReportOverrideEnabled;
  const disableMudurActions = isMudurSocialReport && !mudurSocialReportOverrideEnabled;
  const isInApprovalChain = request.approval_chain?.some(id => String(id) === pid);
  const delegatedFromId = request.current_approver_delegated_from_id != null
    ? String(request.current_approver_delegated_from_id)
    : "";
  const myChainIndex = request.approval_chain?.findIndex(id => String(id) === pid) ?? -1;
  const delegatedChainIndex = delegatedFromId
    ? (request.approval_chain?.findIndex(id => String(id) === delegatedFromId) ?? -1)
    : -1;
  const effectiveChainIndex = myChainIndex >= 0 ? myChainIndex : delegatedChainIndex;
  const isCurrentApprover = request.current_approver_id != null && String(request.current_approver_id) === pid;
  const actionBlockedByReadOnly = isReadOnly && !isCurrentApprover && delegatedFromId !== pid;
  const canEscalateByChain = isCurrentApprover && (isInApprovalChain || delegatedChainIndex >= 0) &&
    effectiveChainIndex >= 0 &&
    effectiveChainIndex < (request.approval_chain?.length ?? 0) - 1;
  // Delegasyon/legacy kayıtlarda approval_chain index'i bulunamazsa, rol bazlı fallback uygula.
  const finalApproverRole = (request.reason_type === "Sosyal İzin" || request.reason_type === "Rapor")
    ? "mudur_yardimcisi"
    : "mudur";
  const currentApproverRoleNorm = normalizeRole(request.current_approver_role || currentApproverPerson?.role || "");
  const canEscalateByRoleFallback =
    isCurrentApprover &&
    Boolean(currentApproverRoleNorm) &&
    currentApproverRoleNorm !== finalApproverRole;
  const canEscalate = canEscalateByChain || canEscalateByRoleFallback;

  if (locallyDeleted) return null;

  return (
    <div className="border-2 border-gray-300 rounded-lg p-4 bg-white shadow-sm hover:border-gray-400 hover:shadow transition-all overflow-hidden">
      {/* Aksiyon kolonu eskiden sağda DİKEY bir sütundu ve flex-shrink-0 olduğu için
          genişliğini kartın en altına kadar rezerve ediyordu; içinde yalnız üstte bir
          rozet olduğundan altında boş bir şerit kalıyor, sol içerik boşuna daralıyordu.
          Artık kolon flex-col ve aksiyonlar order-first ile sağ üstte YATAY bir şerit;
          içerik tam genişlikte akıyor. DOM sırası değişmedi (testid sözleşmesi korunur). */}
      <div className="flex flex-col">
        <div className="min-w-0">
          {/* Personel adı artık üst satırda (aksiyonlarla aynı hizada) — aşağıya bak. */}

          {/* Tarih şeridi: izin aralığı ve işe başlama AYNI ağırlıkta iki çip.
              Eskiden işe başlama 2px SİYAH çerçeveli bir kutuydu; karttaki tek
              siyah öğe olduğu için en baskın şey oydu ve satırı düzensiz
              gösteriyordu. Artık ikisi de yumuşak çerçeveli, hizalı çipler. */}
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {(() => {
              const rangeStart = new Date(request.start_date);
              const rangeEnd = new Date(request.end_date);
              const rangeValid = !Number.isNaN(rangeStart.getTime()) && !Number.isNaN(rangeEnd.getTime());
              const civilServant = Boolean(
                (personnel || []).find(p => p.id === request.personnel_id)?.is_civil_servant
              );
              // Takvimde işaretlenen günler = bakiyeden DÜŞEN günler.
              const chargeableDays = rangeValid
                ? getLeaveChargeableDays(request.start_date, request.end_date, civilServant)
                : [];
              // Aralığın kaç takvim ayına yayıldığı (en fazla 3 ay çizilir ki
              // uzun raporlarda açılır kutu ekrana sığmaz hale gelmesin).
              const monthsSpanned = rangeValid
                ? Math.min(
                    3,
                    Math.max(
                      1,
                      (rangeEnd.getFullYear() - rangeStart.getFullYear()) * 12 +
                        (rangeEnd.getMonth() - rangeStart.getMonth()) + 1
                    )
                  )
                : 1;
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                      title="İzin günlerini takvimde göster"
                    >
                      <Calendar className="h-4 w-4 shrink-0 text-gray-500" />
                      <span className="text-sm font-semibold text-gray-800">
                        {rangeStart.toLocaleDateString('tr-TR')} – {rangeEnd.toLocaleDateString('tr-TR')}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="border-b px-3 py-2">
                      <p className="text-xs text-gray-500">İzin günleri</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {chargeableDays.length} gün izinden düşüyor
                      </p>
                      <p className="mt-1 text-[11px] text-gray-500">
                        {civilServant
                          ? 'Devlet memuru: hafta sonu ve resmî tatiller dâhil tüm günler düşer.'
                          : 'Hafta sonu ve resmî tatiller işaretlenmez, izinden düşmez.'}
                      </p>
                    </div>
                    {/* İzin iki aya taşıyorsa HER İKİ AY birden çizilir; tek ay
                        gösterilince sonraki aya düşen günler işaret alamıyordu.
                        Komşu ayın günleri HİÇ gösterilmez (eylülün altında soluk
                        "1 2 3 4 ekim", ekimin üstünde soluk "28 29 30 eylül"
                        görünmesi kafa karıştırıyordu).
                        cell'e w-8 vermek ŞART: boş hücrenin kendi genişliği yok,
                        varsayılan sınıfla genişlik yalnız gün düğmesinden geliyor.
                        Bu yüzden komşu günler gizlenince satır sola kayıyor ve
                        1 Ekim perşembe yerine pazartesi sütununda çıkıyordu. */}
                    <CalendarView
                      mode="multiple"
                      selected={chargeableDays}
                      defaultMonth={rangeStart}
                      numberOfMonths={monthsSpanned}
                      showOutsideDays={false}
                      classNames={{
                        cell: "relative w-8 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              );
            })()}
            {/* İşe başlama çipi yeşil DEĞİL: "Kalan Yıllık İzin" kutusu zaten yeşil,
                iki yeşil alan birbirine karışıyordu. Nötr koyu çerçeve hem ayırt
                edici hem de çip düzenini bozmuyor. */}
            {request.return_date && (() => {
              const returnDate = new Date(request.return_date);
              if (Number.isNaN(returnDate.getTime())) return null;
              // Tıklayınca takvim açılır: yöneticinin "bu hangi güne denk geliyor,
              // hafta sonuna mı geliyor" sorusunu tarih hesaplamadan yanıtlar.
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-800 bg-white px-2 py-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                      title="Takvimde göster"
                    >
                      <UserCheck className="h-4 w-4 shrink-0 text-gray-700" />
                      <span className="text-sm font-semibold text-gray-900">
                        İşe başlama: {returnDate.toLocaleDateString('tr-TR')}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="border-b px-3 py-2">
                      <p className="text-xs text-gray-500">İşe başlama günü</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {returnDate.toLocaleDateString('tr-TR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    {/* Salt görüntüleme: onSelect verilmediği için gün tıklaması
                        tarihi değiştirmez, yalnız takvimde yerini gösterir. */}
                    {/* Not: `Calendar` adı bu dosyada lucide IKONU olarak kullanılıyor;
                        takvim bileşeni bu yüzden CalendarView olarak import edildi. */}
                    <CalendarView
                      mode="single"
                      selected={returnDate}
                      defaultMonth={returnDate}
                    />
                  </PopoverContent>
                </Popover>
              );
            })()}
          </div>

          {/* Özel değerlendirme / geçmiş gün rozeti — yönetici bu talebin neden
              incelemeye alındığını kartın başında görür. */}
          {reviewModeInfo && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={`text-xs ${reviewModeInfo.className}`}>
                {reviewModeInfo.ruleLabel ? "🕓 " : ""}{reviewModeInfo.label}
              </Badge>
              {reviewModeInfo.ruleLabel && (
                <Badge
                  variant="outline"
                  className="text-xs bg-orange-50 text-orange-700 border-orange-300 cursor-help"
                  title={reviewModeInfo.ruleNote || undefined}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setRuleNoteOpen((v) => !v); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setRuleNoteOpen((v) => !v);
                    }
                  }}
                  aria-expanded={ruleNoteOpen}
                >
                  {reviewModeInfo.ruleLabel} <span aria-hidden="true">ⓘ</span>
                </Badge>
              )}
              {reviewModeInfo.substitutionConflict && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-50 text-amber-800 border-amber-300"
                  title="Bu personelin izin tarihleriyle çakışan aktif vekalet görevi bulunmaktadır. Onaylanırsa vekaletin yeniden atanması gerekir."
                >
                  ⚠️ Aktif vekalet çakışması
                </Badge>
              )}
              {isObserverView && (() => {
                // Bakan kişi zincirin SON karar makamı mı? Yıllık izinde Müdür'ün sırası
                // MY'den sonra gerçekten gelecek; ona "bilgi amaçlı" demek yanıltıcı olur.
                // Sosyal izin/Rapor'da son makam MY olduğundan Müdür'e hiç gelmez.
                const viewerRoleNorm = normalizeRole(userRole);
                const viewerWillDecideLater =
                  Boolean(viewerRoleNorm) &&
                  viewerRoleNorm === finalApproverRole &&
                  viewerRoleNorm !== normalizeRole(currentApproverDisplayRole);
                const stageLabel = getApproverRoleLabel(currentApproverDisplayRole);
                return viewerWillDecideLater ? (
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-300">
                    ⏳ Şu an {stageLabel}&apos;da — sonra sizde
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-300">
                    ℹ️ Bilgi amaçlı — karar {stageLabel}&apos;da
                  </Badge>
                );
              })()}
            </div>
          )}
          {reviewModeInfo?.ruleNote && ruleNoteOpen && (
            <div className="mb-2 rounded border border-orange-200 bg-orange-50 p-2 text-[11px] text-orange-900">
              {reviewModeInfo.ruleNote}
            </div>
          )}
          {reviewModeInfo && request.review_notice && request.status === "in_progress" && (
            <div className="mb-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 whitespace-pre-line">
              {request.review_notice}
            </div>
          )}

          {/* İzin Özeti - Sadece onaylanan veya bekleyen talepler için göster */}
          {/* Sosyal İzin hariç - çünkü yıllık izinden düşmez */}
          {request.start_date && request.end_date && ['approved', 'in_progress'].includes(request.status) && request.reason_type !== "Sosyal İzin" && (() => {
            // calculateLeaveDays fonksiyonunu import et veya burada tanımla
            // Tatil tabloları modül seviyesine taşındı (dosyanın başı) — takvim
            // gösterimi de aynı listeyi kullanıyor.
            const calculateLeaveDays = (startDate, endDate, isCivilServant = false) => {
              const start = new Date(startDate);
              const end = new Date(endDate);

              if (start > end) return { weekdays: 0, weekends: 0, total: 0, workdays: 0 };

              // İzin süresi yalnız [start, end]; return_date sonrası hafta sonu/
              // tatil izne dahil edilmez (cuma tek-gün izinde cmt+paz sayılıp
              // "3 gün" görünmesini önler). returnDateParam artık sayıma girmez.

              let weekdays = 0, weekends = 0, workdays = 0;
              const allHolidays = ALL_DEDUCTION_HOLIDAYS;

              const current = new Date(start);
              while (current <= end) {
                const dateStr = formatLocalDate(current);
                const dayOfWeek = current.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isHoliday = allHolidays.has(dateStr);
                
                if (isWeekend) {
                  weekends++;
                } else {
                  weekdays++;
                  if (!isHoliday) workdays++;
                }
                current.setDate(current.getDate() + 1);
              }

              const total = weekdays + weekends;
              const finalWorkdays = isCivilServant ? total : workdays;
              return { weekdays, weekends, total, workdays: finalWorkdays };
            };

            const person = personnel.find(p => p.id === request.personnel_id);
            const isCivilServant = Boolean(person?.is_civil_servant);
            const calc = calculateLeaveDays(request.start_date, request.end_date, isCivilServant);
            const hasStoredSummary =
              Number.isFinite(Number(request.weekdays)) &&
              Number.isFinite(Number(request.weekends)) &&
              Number.isFinite(Number(request.workdays_used));
            const summaryWeekdays = hasStoredSummary ? Number(request.weekdays) : calc.weekdays;
            const summaryWeekends = hasStoredSummary ? Number(request.weekends) : calc.weekends;
            const summaryWorkdays = hasStoredSummary ? Number(request.workdays_used) : calc.workdays;
            const reasonType = request.reason_type || 'Yıllık İzin';
            // annualPlanned/annualEffective kaldırıldı — tek tüketicisi olan
            // "Planlanan / Fiili düşen" satırı kaldırıldı (bkz. aşağıdaki not).

            const allRequests = Array.isArray(window.__allLeaveRequests) ? window.__allLeaveRequests : [];
            const getUsedDays = (lr) => {
              const raw = lr?.leave_days_used_actual ?? lr?.workdays_used ?? lr?.total_days;
              const n = Number(raw);
              return Number.isFinite(n) && n > 0 ? n : 0;
            };
            const approvedForPerson = allRequests.filter((lr) =>
              lr?.personnel_id === request.personnel_id &&
              lr?.status === 'approved'
            );
            const approvedAnnualUsed = approvedForPerson
              .filter((lr) => ['Yıllık İzin', 'Diğer'].includes(lr?.reason_type))
              .reduce((sum, lr) => sum + getUsedDays(lr), 0);
            const approvedSocialUsed = approvedForPerson
              .filter((lr) => lr?.reason_type === 'Sosyal İzin')
              .reduce((sum, lr) => sum + getUsedDays(lr), 0);
            const approvedReportUsed = approvedForPerson
              .filter((lr) => lr?.reason_type === 'Rapor')
              .reduce((sum, lr) => sum + getUsedDays(lr), 0);
            
            // İzin türüne göre toplam hak ve kullanılan günler
            let totalLeaveRight = 0;
            let usedDays = 0;
            let leaveLabel = '';
            
            if (reasonType === 'Yıllık İzin') {
              totalLeaveRight = person?.annual_leave_days || 20;
              usedDays = Math.max(person?.used_leave_days || 0, approvedAnnualUsed);
              leaveLabel = 'Yıllık İzin';
            } else if (reasonType === 'Sosyal İzin') {
              totalLeaveRight = 12;
              usedDays = Math.max(person?.used_social_leave_days || 0, approvedSocialUsed);
              leaveLabel = 'Sosyal İzin';
            } else if (reasonType === 'Rapor') {
              totalLeaveRight = 40;
              usedDays = Math.max(person?.used_report_days || 0, approvedReportUsed);
              leaveLabel = 'Rapor';
            } else {
              // Diğer izin türleri için yıllık izin gibi davran
              totalLeaveRight = person?.annual_leave_days || 20;
              usedDays = Math.max(person?.used_leave_days || 0, approvedAnnualUsed);
              leaveLabel = 'İzin';
            }
            
            // Kalan izin hesabı (bu talep dahilse düş)
            const remainingBefore = totalLeaveRight - usedDays;
            const remainingAfter = request.status === 'approved' 
              ? remainingBefore // Zaten düşülmüş
              : remainingBefore - summaryWorkdays; // Onaylanırsa düşecek
            
            return (
              <div className="mb-2 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
                <div className="mb-2">
                  <span className="text-xs font-semibold text-blue-900">📊 İzin Özeti</span>
                  {isCivilServant && (
                    <span className="ml-2 text-[10px] text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                      Devlet Memuru
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-[10px]">Hafta İçi</p>
                    <p className="font-bold text-green-600">{summaryWeekdays} gün</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-[10px]">Hafta Sonu</p>
                    <p className="font-bold text-orange-600">{summaryWeekends} gün</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-gray-600 text-[10px]">Toplam</p>
                    <p className="font-bold text-purple-600">{summaryWeekdays + summaryWeekends} gün</p>
                  </div>
                </div>
                {/* "… Hakkı" burada gösterilir: kalan gün ile AYNI hesaptan (totalLeaveRight)
                    gelir, böylece iki sayı hiçbir zaman birbiriyle çelişmez. */}
                {request.status === 'approved' ? (
                  <div className="bg-green-50 border border-green-300 rounded p-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <p className="text-xs font-bold text-green-800">
                      Kalan {leaveLabel}: {remainingBefore} gün
                    </p>
                    <p className="text-xs font-semibold text-green-700">
                      {leaveLabel} Hakkı: {totalLeaveRight} gün
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-2">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                      <p className="text-xs font-bold text-yellow-800">
                        Onaylanırsa Kalan {leaveLabel}: {remainingBefore} - {summaryWorkdays} = {remainingAfter} gün
                      </p>
                      <p className="text-xs font-semibold text-yellow-800">
                        {leaveLabel} Hakkı: {totalLeaveRight} gün
                      </p>
                    </div>
                    <p className="text-[10px] text-gray-600 mt-1">
                      (Şu anki kalan {leaveLabel.toLowerCase()}: {remainingBefore} gün)
                    </p>
                  </div>
                )}
                {/* "Planlanan / Fiili düşen" satırı KALDIRILDI: bu iki değeri tutan
                    annual_days_planned / annual_days_effective sütunları veritabanında
                    boş geliyor ve satır her kayıtta "0 gün • 0 gün" yazıyordu — kartın
                    geri kalanıyla (25 hak → 19 kalan = 6 gün düşmüş) açıkça çelişiyordu.
                    Rapor çakışmasında düşümün azaldığını göstermek isteniyorsa bu bir
                    BACKEND işi: sütunlar dolduktan sonra satır geri getirilebilir. */}
              </div>
            );
          })()}

          {/* Sosyal İzin Bilgilendirmesi */}
          {request.reason_type === "Sosyal İzin" && ['approved', 'in_progress'].includes(request.status) && (
            <div className="mb-2 bg-purple-50 border border-purple-300 rounded-lg p-3">
              <p className="text-xs font-semibold text-purple-900">
                ℹ️ Sosyal İzin - Yıllık izin hakkından düşmez
              </p>
            </div>
          )}

          {/* İzin türü artık üst satırda, personel adının yanında rozet olarak. */}
          {showMudurSocialReportNote && request.status === 'in_progress' && (
            <Badge variant="outline" className="bg-gray-100 border-gray-300 text-gray-700 text-xs mb-2">
              Karar varsayılan olarak MY’da
            </Badge>
          )}

          {/* Leave Detail */}
          {request.reason_detail && (
            <p className="text-xs text-gray-500 italic mb-2">&quot;{request.reason_detail}&quot;</p>
          )}

          {/* Rapor Fotoğrafı - Sadece "Rapor" türü için */}
          {request.reason_type === "Rapor" && request.report_file && (
            <div className="mb-3 border-2 border-blue-300 rounded-lg p-3 bg-blue-50">
              <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Rapor Fotoğrafı
              </p>
              {/* Tıklayınca uygulama içindeki görüntüleyici açılır (yakınlaştır /
                  döndür / kaydır). Eskiden window.open ile yeni sekmede ham dosya
                  açılıyordu; onay ekranından çıkmak gerekiyordu. */}
              <button
                type="button"
                onClick={() => setReportViewerOpen(true)}
                title="Raporu büyüt"
                className="block w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <img
                  src={request.report_file}
                  alt="Rapor"
                  className="w-full max-h-48 object-contain rounded-lg border border-blue-300 cursor-zoom-in hover:opacity-90 transition-opacity"
                />
              </button>
              <div className="mt-2 flex items-center justify-between gap-2">
                {request.report_filename ? (
                  <p className="text-xs text-blue-700 truncate">📄 {request.report_filename}</p>
                ) : <span />}
                <button
                  type="button"
                  onClick={() => setReportViewerOpen(true)}
                  className="shrink-0 text-xs font-medium text-blue-700 hover:underline"
                >
                  🔍 Büyüt
                </button>
              </div>
              <ImageLightbox
                open={reportViewerOpen}
                onClose={() => setReportViewerOpen(false)}
                src={request.report_file}
                alt={`Rapor — ${request.personnel_name || ''}`.trim()}
                filename={request.report_filename}
              />
            </div>
          )}

          {/* Talep zamanı üst satıra, izin türü rozetinin yanına taşındı. */}

          {/* İncelemede olan kişi bilgisi */}
          {request.status === "in_progress" && currentApproverDisplayName && (
            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
              <p>
                <span className="font-semibold">İncelemede:</span>{" "}
                {getApproverRoleLabel(currentApproverDisplayRole)} {currentApproverDisplayName}
              </p>
              {request.current_approver_delegated_from_name && (
                <p className="text-[11px] text-amber-700">
                  Vekalet veren: {getApproverRoleLabel(request.current_approver_delegated_from_role)} {request.current_approver_delegated_from_name}
                </p>
              )}
            </div>
          )}

          {/* Hiyerarşi bozukluğu uyarısı */}
          {request.status === "in_progress" && request.hierarchy_warning && (
            <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded p-2 text-xs text-yellow-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{request.hierarchy_warning}</span>
            </div>
          )}

          {canViewDelegateReasonDetails && (showPersonnelName || showActionButtons) && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-800">{requesterRoleLabel} önerisi</p>
              {requestedParkDelegateList.length > 0 ? (
                <div className="space-y-1">
                  {requestedParkDelegateList.map((it, idx) => (
                    <div key={`${it.park_id || idx}_requested`} className="text-xs text-blue-900">
                      {it.park_name}: <span className="font-medium">{it.delegate_name || "Seçilmedi"}</span>
                      {String(it?.change_reason || "").trim() && (
                        <div className="text-[11px] text-gray-600 mt-0.5">
                          Açıklama: {it.change_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-blue-900">
                  {requestedSingleDelegateId
                    ? `${requesterRoleLabel} önerisi: ${requestedSingleDelegateName || "İsim bulunamadı"}`
                    : `${requesterRoleLabel} önerisi yok`}
                  {String(request?.requestedSubstituteReason || "").trim() && (
                    <div className="text-[11px] text-gray-600 mt-1">
                      Açıklama: {request.requestedSubstituteReason}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs font-semibold text-blue-800 pt-1">Vekil Planı Geçmişi</p>
              {delegatePlanHistoryEntries.length > 0 ? (
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {delegatePlanHistoryEntries.map((entry, idx) => (
                    <div key={`${entry?.ts || idx}_${entry?.changedById || ""}`} className="bg-white border border-blue-100 rounded p-2">
                      <div className="text-xs font-semibold text-blue-900">
                        {entry?.stageRole || "AŞAMA"} {entry?.changedByName ? `- ${entry.changedByName}` : ""}
                        {entry?.isDraft ? " (taslak)" : ""}
                        {entry?.onBehalfOfName ? ` (${entry.onBehalfOfName} adına)` : ""}
                      </div>
                      {entry?.ts && (
                        <div className="text-[10px] text-gray-500">
                          {new Date(entry.ts).toLocaleString("tr-TR")}
                        </div>
                      )}
                      {(() => {
                        const changes = (Array.isArray(entry?.changes)
                          ? entry.changes
                          : (Array.isArray(entry?.changeSummary) ? entry.changeSummary : []))
                          .slice()
                          .sort((a, b) => {
                            const an = String(a?.parkName || parkIdNameMap.get(String(a?.parkId || "")) || "");
                            const bn = String(b?.parkName || parkIdNameMap.get(String(b?.parkId || "")) || "");
                            return an.localeCompare(bn, "tr");
                          });
                        const showNoChange = Boolean(entry?.noChange) || changes.length === 0;
                        return !showNoChange ? (
                        <div className="mt-1 space-y-1">
                          {changes.map((chg, cidx) => (
                            <div key={`${chg?.parkId || cidx}_chg`} className="text-[11px] text-gray-700">
                              <div>
                                {(() => {
                                  const rawId = String(chg?.parkId || "");
                                  const fallbackName = rawId ? (parkIdNameMap.get(rawId) || "") : "";
                                  const safeParkName = chg?.parkName || fallbackName || (rawId ? rawId.slice(0, 8) : "Park");
                                  const oldName = chg?.oldDelegateName || chg?.old?.delegateName || "Seçilmedi";
                                  const newName = chg?.newDelegateName || chg?.new?.delegateName || "Seçilmedi";
                                  return `${safeParkName}: ${oldName} → ${newName}`;
                                })()}
                              </div>
                              {String(chg?.reason || "").trim() && (
                                <div className="text-[11px] text-gray-500">Sebep: {chg.reason}</div>
                              )}
                            </div>
                          ))}
                        </div>
                        ) : (
                        <div className="text-[11px] text-gray-500 mt-1">Değişiklik yapılmadı</div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500">Kayıt bulunmuyor.</div>
              )}

              <p className="text-xs font-semibold text-blue-800 pt-1">Mevcut vekil planı</p>
              {currentDelegationPlan?.mode === "PER_PARK" && effectiveParkSubstitutes.length > 0 ? (
                <div className="space-y-1">
                  {effectiveParkSubstitutes.map((ps, idx) => (
                    <div key={`${ps.park_id || idx}_plan`} className="text-xs text-blue-900">
                      {ps.park_name || "Park"}: <span className="font-medium">{ps.substitute_name || "Seçilmedi"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-blue-900">
                  Plan: {currentPlanSingleDelegateName || "Seçilmedi"}
                </div>
              )}
            </div>
          )}


          {/* Park/Bölge ve Vekil Bilgileri - Sadece manager view için */}
          {showPersonnelName && (normalizeRole(request.personnel_role) === 'park_gorevlisi' || normalizeRole(request.personnel_role) === 'cim_ekibi') && (
            <div className="mt-3 space-y-2">
              {/* Sorumlu Parklar ve Vekilleri */}
              {personnelParks.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-amber-600" />
                      <span className="text-xs font-semibold text-amber-800">{request.personnel_name}'ın Parkları:</span>
                    </div>
                    {/* Park bazlı vekil düzenleme butonu - SADECE sıra kendisinde olan kişi için */}
                    {request.status === 'in_progress' && isCurrentApprover && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs h-6 px-2"
                        onClick={async () => {
                          if (!request?.id) {
                            toast.error("Talep bulunamadı.");
                            return;
                          }
                          await initializeParkSubstitutes();
                        }}
                        disabled={editBlockedByReadOnly}
                      >
                        ✏️ Vekil Düzenle
                      </Button>
                    )}
                  </div>
                  
                  {/* Park listesi - her park için vekil ve sebep göster */}
                  <div className="space-y-2">
                    {personnelParks.map(park => {
                      // Bu park için atanmış vekili ve sebebi bul
                      const parkSub = (effectiveParkSubstitutes || []).find(ps => ps.park_id === park.id);
                      const requestedParkSub = (Array.isArray(request?.requestedParkSubstitutes) ? request.requestedParkSubstitutes : [])
                        .find(ps => String(ps?.park_id || "") === String(park.id || ""));
                      const substituteForPark =
                        parkSub?.substitute_name ||
                        requestedParkSub?.delegate_name ||
                        requestedParkSub?.substitute_name ||
                        currentPlanSingleDelegateName ||
                        requestedSingleDelegateName ||
                        request.substitute_personnel_name;
                      const reasonForPark = parkSub?.change_reason || requestedParkSub?.change_reason || requestedParkSub?.reason;
                      // changed_by_name boşsa (eski kayıt) geçmişten türet.
                      const changedByForPark = parkSub?.changed_by_name || changedByByPark[String(park.id)] || "";
                      
                      return (
                        <div key={park.id} className="bg-white rounded-lg p-3 border border-amber-200">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-amber-800">🌳 {park.name}</span>
                            {substituteForPark ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                → {substituteForPark}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs text-gray-400">Vekil seçilmedi</Badge>
                            )}
                          </div>
                          {/* Park için son değişiklik sebebi ve değiştiren kişi */}
                          {canViewDelegateReasonDetails && ((reasonForPark) || changedByForPark) && (
                            <div className="mt-1 text-xs text-gray-500">
                              {changedByForPark && (
                                <span className="font-medium text-blue-600">({changedByForPark})</span>
                              )}
                              {canViewDelegateReasonDetails && reasonForPark && (
                                <span className="italic ml-1">"{reasonForPark}"</span>
                              )}
                            </div>
                          )}
                          
                          {/* Bu park için tüm değişiklik geçmişi */}
                          {canViewDelegateReasonDetails && (() => {
                            const parkHistory = parkSubstituteHistoryEntries.filter(h => h.park_id === park.id);
                            if (parkHistory.length === 0) return null;
                            return (
                              <div className="mt-2 pt-2 border-t border-amber-100 space-y-1">
                                <p className="text-xs text-amber-700 font-medium">📜 Değişiklik Geçmişi:</p>
                                {parkHistory.map((change, idx) => {
                                  const { beforeName, afterName } = resolveHistoryTransitionNames(change);
                                  return (
                                    <div key={`${change.at || idx}_${change.actor_id || ''}`} className="text-xs text-gray-600 pl-2 border-l-2 border-amber-200">
                                      <span className="font-medium text-blue-600">{change.actor_name || 'Bilinmiyor'}:</span>
                                      <span> {beforeName} → {afterName}</span>
                                      {canViewDelegateReasonDetails && change.reason && (
                                        <span className="italic text-gray-500"> "{change.reason}"</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                  
                </div>
              )}
            </div>
          )}

          {/* Formen/BM/MY için Bölge + Vekil düzenleme */}
          {(showPersonnelName || showActionButtons) && ['formen','bolge_muhendisi','mudur_yardimcisi'].includes(normalizeRole(request.personnel_role)) && (
            <div className="mt-3 space-y-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800">{request.personnel_name}'ın Bölgeleri:</span>
                  </div>
                  {request.status === 'in_progress' && isCurrentApprover && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-xs h-6 px-2"
                      onClick={async () => {
                        setSimpleSubId(currentPlanSingleDelegateId || "");
                        setSimpleReason("");
                        setSimpleDirty(false);
                        await loadSubstituteEligibility();
                        setShowSimpleSubDialog(true);
                      }}
                      disabled={editBlockedByReadOnly}
                    >
                      ✏️ Vekil Düzenle
                    </Button>
                  )}
                </div>
                {personnelRegions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {personnelRegions.map(region => (
                      <Badge key={region.id} variant="outline" className="text-xs text-amber-700 border-amber-300">
                        {region.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Bölge bulunamadı.</div>
                )}
                <div className="mt-2 text-xs text-gray-600">
                  Vekil: {currentPlanSingleDelegateName || 'Seçilmedi'}
                </div>
                {canViewDelegateReasonDetails && parkSubstituteHistoryEntries.length > 0 && (() => {
                  const firstChange = parkSubstituteHistoryEntries[parkSubstituteHistoryEntries.length - 1];
                  const lastChange = parkSubstituteHistoryEntries[0];
                  return (
                    <div className="mt-2 text-xs text-gray-600">
                      İlk değişiklik: {resolveHistoryTransitionNames(firstChange).beforeName} → {resolveHistoryTransitionNames(firstChange).afterName}
                      {firstChange?.actor_name ? ` (${firstChange.actor_name})` : ''}
                      {lastChange && lastChange !== firstChange && (
                        <> · Son değişiklik: {lastChange?.actor_name || 'Bilinmiyor'}{(canViewDelegateReasonDetails && lastChange?.reason) ? ` — ${lastChange.reason}` : ''}</>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Basit Vekil Düzenleme Dialog (Formen/BM/MY) */}
          {showSimpleSubDialog && (
            <DraggableModal
              isOpen={showSimpleSubDialog}
              onClose={() => {
                setShowSimpleSubDialog(false);
                setSimpleDirty(false);
              }}
              title="✏️ Vekil Düzenle"
              subtitle={`${request.personnel_name} için vekil seçin`}
            >
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Vekil:</label>
                  <SearchableSelect
                    className="w-full text-sm"
                    value={simpleSubId}
                    onChange={(v) => {
                      setSimpleSubId(v);
                      setSimpleDirty(true);
                    }}
                    disabled={updatingSubstitute}
                    allValue=""
                    allLabel="Vekil seçin..."
                    searchPlaceholder="Personel ara..."
                    options={availableSubstitutesWithEligibility.map((sub) => ({
                      id: sub.id,
                      name: `${sub.name}${sub.isUnavailableForRange ? ` (${sub.unavailableText})` : ""}`,
                      disabled: sub.isUnavailableForRange,
                      title: sub.unavailableText || undefined,
                    }))}
                  />
                  {substituteEligibilityLoading && (
                    <p className="text-[11px] text-gray-500 mt-1">Uygunluk kontrol ediliyor...</p>
                  )}
                  {substituteEligibilityError && (
                    <div className="mt-2 rounded border border-red-300 bg-red-50 p-2" data-testid="eligibility-error-simple">
                      <p className="text-[11px] font-semibold text-red-800">Vekil uygunluk bilgisi yüklenemedi.</p>
                      <p className="text-[10px] text-red-700">
                        Güvenlik için vekil seçimi kapatıldı (izinli personel yanlışlıkla seçilmesin).{" "}
                        <button type="button" className="underline" disabled={substituteEligibilityLoading} onClick={() => loadSubstituteEligibility()}>
                          Tekrar dene
                        </button>
                      </p>
                    </div>
                  )}
                  {!substituteEligibilityError && availableSubstitutesWithEligibility.some((s) => s.isUnavailableForRange) && (
                    <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2">
                      <p className="text-[11px] font-semibold text-amber-900 mb-1">İzinde olan adaylar:</p>
                      <div className="space-y-1">
                        {availableSubstitutesWithEligibility
                          .filter((s) => s.isUnavailableForRange)
                          .map((s) => (
                            <p key={s.id} className="text-[11px] text-amber-800">
                              {s.name}: {s.unavailableText}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}
                  {(() => {
                    // Mevcut vekil sonradan izne çıkmışsa uyar (otomatik değiştirme yok).
                    const current = availableSubstitutesWithEligibility.find(
                      (s) => String(s.id) === String(simpleSubId || "")
                    );
                    if (!current?.isUnavailableForRange) return null;
                    return (
                      <p className="text-[11px] font-semibold text-red-600 mt-2">
                        ⚠️ Seçili vekil ({current.name}) artık uygun değil: {current.unavailableText || "izinli"}. Lütfen başka vekil seçin.
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Açıklama (opsiyonel):</label>
                  <textarea
                    className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                    rows={3}
                    value={simpleReason}
                    onChange={(e) => {
                      setSimpleReason(e.target.value);
                      setSimpleDirty(true);
                    }}
                    placeholder="Değişiklik nedeni..."
                    disabled={updatingSubstitute}
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSimpleSubDialog(false);
                      setSimpleDirty(false);
                    }}
                    disabled={updatingSubstitute}
                  >
                    İptal
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                  onClick={async () => {
                      await handleSubstituteChange(simpleSubId || null, simpleReason);
                    }}
                  disabled={updatingSubstitute || editBlockedByReadOnly || !simpleDirty}
                  >
                    Kaydet
                  </Button>
                </div>
              </div>
            </DraggableModal>
          )}

          {/* Park Bazlı Vekil Düzenleme Dialog - Sürüklenebilir */}
          {showParkSubstituteDialog && (
            <ParkSubstituteEditDialog
              isOpen={showParkSubstituteDialog}
              onClose={() => setShowParkSubstituteDialog(false)}
              personnelName={request.personnel_name}
              initialParkSubstitutes={parkSubstitutes}
              availableSubstitutes={availableSubstitutesWithEligibility}
              eligibilityError={substituteEligibilityError}
              eligibilityLoading={substituteEligibilityLoading}
              onReloadEligibility={loadSubstituteEligibility}
              onSave={saveParkSubstitutes}
              setParkSubstitutes={setParkSubstitutes}
              updatingSubstitute={updatingSubstitute}
            />
          )}

          {showDeleteConfirmDialog && (
            <DraggableModal
              isOpen={showDeleteConfirmDialog}
              onClose={() => {
                if (deleteSubmitting) return;
                setShowDeleteConfirmDialog(false);
                setDeleteConfirmText('');
              }}
              title="🗑️ İzin Talebini Sil"
              subtitle="Bu pencereyi başlıktan tutup sürükleyebilirsiniz."
              maxWidth="max-w-md"
            >
              <div className="space-y-4">
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  Bu izin talebini kalıcı olarak silmek istediğinizden emin misiniz?
                </div>
                <div className="text-xs text-gray-600">
                  Personel: <span className="font-semibold">{request.personnel_name}</span>
                </div>

                <DeleteNameConfirm
                  name={request.personnel_name}
                  value={deleteConfirmText}
                  onChange={setDeleteConfirmText}
                  label="personelin adını"
                />

                <div className="flex items-center justify-end gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirmDialog(false);
                      setDeleteConfirmText('');
                    }}
                    disabled={deleteSubmitting}
                  >
                    Vazgeç
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={handleDeleteConfirmSubmit}
                    disabled={deleteSubmitting || !deleteNameMatches(deleteConfirmText, request.personnel_name)}
                  >
                    {deleteSubmitting ? "Siliniyor..." : "Evet, Sil"}
                  </Button>
                </div>
              </div>
            </DraggableModal>
          )}

          {/* Vekil Değişiklik Geçmişi - vekil düzenle kartının altında */}
          {canViewDelegateReasonDetails && parkSubstituteHistoryEntries.length > 0 && (
            <div className="mt-3 overflow-hidden">
              <p className="text-xs font-semibold text-gray-700 mb-2">Vekil Değişiklik Geçmişi:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {parkSubstituteHistoryEntries.map((h, idx) => (
                  <div key={`${h.at || idx}_${h.park_id || ''}`} className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="font-semibold text-gray-700">
                      {h.actor_name || 'Bilinmiyor'}
                    </div>
                    <div className="mt-1 text-[11px] text-gray-700">
                      {(h.park_name || 'Park')}: {resolveHistoryTransitionNames(h).beforeName} → {resolveHistoryTransitionNames(h).afterName}
                    </div>
                    {canViewDelegateReasonDetails && h.reason && (
                      <div className="text-[11px] text-gray-500">Açıklama: {h.reason}</div>
                    )}
                    {h.at && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        {new Date(h.at).toLocaleString('tr-TR')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approval History */}
          {filteredHistory.length > 0 && (
            <div className="mt-3 overflow-hidden">
              <p className="text-xs font-semibold text-gray-700 mb-2">İşlem Geçmişi ({filteredHistory.length}):</p>
              {showPersonnelName ? (
                // Compact vertical list for manager view
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filteredHistory.map((step, index) => {
                    // Karar türünü belirle
                    const hasRejectionComment = step.comment && step.comment.startsWith("RED ÖNERİSİ:");
                    const isApproval = step.action === 'approved' || step.action === 'approve';
                    const isRejection = step.action === 'rejected' || step.action === 'reject';
                    const isRejectAndForward = step.action === 'reject_and_forward';
                    const isForward = step.action === 'forward' || step.action === 'escalate';
                    
                    // Onaylayan kişinin rolünü bul
                    const approverPerson = personnel.find(p => p.id === step.approver_id);
                    
                    // İzin türüne göre son onay makamını belirle
                    // Yıllık İzin: Son onay = Müdür (admin)
                    // Sosyal İzin, Rapor: Son onay = Müdür Yardımcısı
                    const leaveType = request.reason_type;
                    let isFinalApprover = false;
                    if (leaveType === 'Yıllık İzin') {
                      // Yıllık izinde sadece Müdür son onay makamı
                      isFinalApprover = approverPerson?.role === 'mudur';
                    } else {
                      // Sosyal İzin, Rapor, Diğer için Müdür Yardımcısı veya Müdür son onay makamı
                      isFinalApprover = approverPerson?.role === 'mudur_yardimcisi' || approverPerson?.role === 'mudur';
                    }
                    
                    // Gerçek yorumu çıkar (otomatik prefix'leri kaldır)
                    let realComment = null;
                    if (step.comment) {
                      let cleanComment = step.comment;
                      // RED ÖNERİSİ: prefix'ini kaldır
                      if (cleanComment.startsWith("RED ÖNERİSİ:")) {
                        cleanComment = cleanComment.replace(/^RED ÖNERİSİ:\s*/, '');
                      }
                      // Otomatik yorumları filtrele
                      if (cleanComment && 
                          !cleanComment.startsWith("Approved by") && 
                          !cleanComment.startsWith("Escalating to") &&
                          cleanComment.trim().length > 0) {
                        realComment = cleanComment;
                      }
                    }
                    
                    let decisionText = "";
                    let decisionColor = "";
                    let bgColor = "";
                    let borderColor = "";
                    
                    if (isApproval && !isForward) {
                      // Müdür Yardımcısı ise son onay makamı - sadece "Onayladı" yaz
                      if (isFinalApprover) {
                        decisionText = "✅ Onayladı";
                      } else {
                        decisionText = "✅ Onaylayarak Üst Makama Sevk Etti";
                      }
                      decisionColor = "text-green-700";
                      bgColor = "bg-green-50";
                      borderColor = "border-l-green-500";
                    } else if (isRejectAndForward || hasRejectionComment) {
                      decisionText = "❌ Reddet ile Üst Makama Sevk Etti.";
                      decisionColor = "text-red-700";
                      bgColor = "bg-red-50";
                      borderColor = "border-l-red-500";
                    } else if (isRejection) {
                      decisionText = "❌ Reddetti";
                      decisionColor = "text-red-700";
                      bgColor = "bg-red-50";
                      borderColor = "border-l-red-500";
                    } else if (isForward) {
                      decisionText = "⬆️ Üst Makama Sevk Etti";
                      decisionColor = "text-blue-700";
                      bgColor = "bg-blue-50";
                      borderColor = "border-l-blue-500";
                    } else {
                      decisionText = "⬆️ Üst Makama Sevk Etti";
                      decisionColor = "text-blue-700";
                      bgColor = "bg-blue-50";
                      borderColor = "border-l-blue-500";
                    }
                    
                    return (
                      <div 
                        key={index} 
                        className={`p-2 rounded text-xs ${bgColor} ${borderColor}`}
                        style={{ borderLeftWidth: '3px', borderLeftStyle: 'solid' }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{step.approver_name}</span>
                          <span className="text-[10px] text-gray-500">
                            {new Date(step.timestamp).toLocaleString('tr-TR')}
                          </span>
                        </div>
                        {step.delegated_from_name && (
                          <p className="text-[11px] text-amber-700 mt-1">
                            Vekalet veren: {getApproverRoleLabel(step.delegated_from_role)} {step.delegated_from_name}
                          </p>
                        )}
                        <p className={`font-semibold mt-1 ${decisionColor}`}>
                          {decisionText}
                        </p>
                        {realComment && (
                          <p className="text-gray-600 mt-1 italic text-[11px] bg-white/50 p-1 rounded">
                            {realComment}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Vertical cards for employee view
                <div className="space-y-2">
                  {filteredHistory.map((step, index) => (
                    <div key={index} className="p-2 bg-gray-50 border-l-4 border-gray-300 rounded text-xs">
                      <p className="font-semibold text-gray-800">
                        {step.approver_name} - {
                          step.action === "approved" || step.action === "approve" ? "✅ Onayladı" :
                          step.action === "rejected" || step.action === "reject" ? "❌ Reddetti" :
                          step.action === "reject_and_forward" ? "❌ Reddet ile Üst Makama Sevk Etti." :
                          step.action === "forward" || step.action === "escalate" ? "⬆️ Üst Makama Sevk Etti" :
                          step.comment && step.comment.startsWith("RED ÖNERİSİ:") ? "❌ Reddet ile Üst Makama Sevk Etti." :
                          "⬆️ Üst Makama Sevk Etti"
                        }
                      </p>
                      {step.delegated_from_name && (
                        <p className="text-amber-700 mt-1">
                          Vekalet veren: {getApproverRoleLabel(step.delegated_from_role)} {step.delegated_from_name}
                        </p>
                      )}
                      {step.comment && (
                        <p className="text-gray-600 italic mt-1">&quot;{step.comment.replace(/^RED ÖNERİSİ:\s*/, '')}&quot;</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* Right Side - Status Badge or Action Buttons */}
        <div className="order-first flex flex-wrap items-center gap-2 mb-2">
          {showPersonnelName && (
            <div className="flex min-w-0 items-center gap-2">
              <Users className="h-4 w-4 shrink-0 text-gray-500" />
              {typeof onPersonnelClick === 'function' ? (
                <button
                  type="button"
                  onClick={() => onPersonnelClick(request)}
                  title="Personelin izin durumunu aç (yıllık / sosyal / rapor)"
                  className="font-semibold text-left text-blue-700 hover:text-blue-900 hover:underline underline-offset-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  {request.personnel_name}
                </button>
              ) : (
                <span className="font-semibold">{request.personnel_name}</span>
              )}
            </div>
          )}
          {/* İzin türü — eskiden kartın ortasında ayrı bir satırdı; buraya alınınca
              hem bir satır yer kazanıldı hem de kartı tararken tür hemen görünüyor. */}
          {(request.reason_type || request.reason) && (
            <Badge
              variant="outline"
              className={`text-xs font-semibold ${
                (request.reason_type || request.reason) === "Yıllık İzin"
                  ? "bg-blue-50 text-blue-800 border-blue-300"
                  : (request.reason_type || request.reason) === "Sosyal İzin"
                  ? "bg-purple-50 text-purple-800 border-purple-300"
                  : (request.reason_type || request.reason) === "Rapor"
                  ? "bg-orange-50 text-orange-800 border-orange-300"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              {request.reason_type || request.reason}
            </Badge>
          )}
          {/* Talep zamanı: rozetin yanında, ikincil ağırlıkta. Saniye yalnız
              üzerine gelince (title) görünür — üst satır kalabalıklaşmasın. */}
          {(() => {
            const created = request.created_at ? new Date(request.created_at) : null;
            if (!created || Number.isNaN(created.getTime())) return null;
            return (
              <span
                className="text-[11px] text-gray-400"
                title={`Talep: ${created.toLocaleString('tr-TR', { hour12: false })}`}
              >
                Talep: {created.toLocaleDateString('tr-TR')}{" "}
                {created.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
            );
          })()}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          {/* Delete Button - Role + leave type permission */}
          {showDeleteButton && canDeleteRequest && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWithConfirm();
              }}
              disabled={isReadOnly && !canDeleteRequest}
              title="Sil"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          
          {/* For manager view with action buttons */}
          {showActionButtons && (
            (() => {
              // If request is completed
              if (['approved', 'rejected', 'cancelled'].includes(request.status)) {
                return (
                  <Badge 
                    className={`text-xs px-2 py-1 font-bold whitespace-nowrap ${
                      request.status === 'approved' ? 'bg-green-500 hover:bg-green-600 text-white' :
                      request.status === 'rejected' ? 'bg-red-500 hover:bg-red-600 text-white' :
                      'bg-gray-500 hover:bg-gray-600 text-white'
                    }`}
                  >
                    {request.status === 'approved' ? '✅' :
                     request.status === 'rejected' ? '❌' :
                     '🚫'}
                  </Badge>
                );
              }
              
              // If already acted
              if (hasAlreadyActed) {
                return (
                  <div className="flex items-center justify-center p-3 bg-gray-100 rounded text-xs text-gray-600">
                    İşlem yapıldı
                  </div>
                );
              }
              
              // If not current approver (Müdür override hariç)
              if (!isCurrentApprover && !isMudurSocialReport) {
                return null;
              }
              
              const handleAction = (action) => {
                if (disableMudurActions || actionBlockedByReadOnly) return;
                if (!onActionClick) {
                  toast.error("Onay paneli bağlantısı kurulamadı. Sayfayı yenileyin.");
                  return;
                }
                onActionClick(request, action);
              };
              return (
                <div className="flex flex-row flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`card-approve-btn-${request.id}`}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleAction("approve"); }}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={disableMudurActions || actionBlockedByReadOnly}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Onayla
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    data-testid={`card-reject-btn-${request.id}`}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleAction("reject"); }}
                    disabled={disableMudurActions || actionBlockedByReadOnly}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reddet
                  </Button>
                  {canEscalate && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleAction("escalate"); }}
                      disabled={disableMudurActions || actionBlockedByReadOnly}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Üst Makama Sevk
                    </Button>
                  )}
                </div>
              );
            })()
          )}

          {/* For employee view - status badges */}
          {!showActionButtons && (
            <>
              {request.status === "in_progress" && (
                <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700">
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>İncelemede</span>
                    </div>
                    {currentApproverDisplayName && (
                      <div className="space-y-0.5">
                        <div className="text-[10px] leading-3 font-semibold text-blue-800">
                          {getApproverRoleLabel(currentApproverDisplayRole)} {currentApproverDisplayName}
                        </div>
                        {request.current_approver_delegated_from_name && (
                          <div className="text-[10px] leading-3 text-amber-700">
                            Vekalet veren: {getApproverRoleLabel(request.current_approver_delegated_from_role)} {request.current_approver_delegated_from_name}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Badge>
              )}
              {request.status === "approved" && (
                <Badge variant="outline" className="bg-green-50 border-green-300 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Onaylandı
                </Badge>
              )}
              {request.status === "rejected" && (
                <Badge variant="outline" className="bg-red-50 border-red-300 text-red-700">
                  <XCircle className="h-3 w-3 mr-1" />
                  Reddedildi
                </Badge>
              )}
              {request.status === "cancelled" && (
                <Badge variant="outline" className="bg-gray-50 border-gray-300 text-gray-700">
                  <XCircle className="h-3 w-3 mr-1" />
                  İptal Edildi
                </Badge>
              )}

              {/* Cancel button for employee */}
              {showCancelButton && request.status === "in_progress" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 border-red-300"
                  onClick={handleCancel}
                >
                  <XCircle className="h-3 w-3 mr-1" />
                  İptal Et
                </Button>
              )}
            </>
          )}
          </div>
        </div>
      </div>
      
    </div>
  );
};

// Park Vekil Düzenleme Dialog - Ayrı component (performans için)
const ParkSubstituteEditDialog = ({
  isOpen,
  onClose,
  personnelName,
  initialParkSubstitutes,
  availableSubstitutes,
  eligibilityError = false,
  eligibilityLoading = false,
  onReloadEligibility,
  onSave,
  setParkSubstitutes,
  updatingSubstitute
}) => {
  const [localParks, setLocalParks] = useState(initialParkSubstitutes || []);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !wasOpen.current && initialParkSubstitutes?.length) {
      setLocalParks(initialParkSubstitutes);
      setDirty(false);
    }
    wasOpen.current = isOpen;
    if (!isOpen) {
      setSaving(false);
      setDirty(false);
    }
  }, [isOpen, initialParkSubstitutes]);

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log("save clicked");
    if (!onSave || saving || updatingSubstitute) return;
    setSaving(true);
    try {
      await onSave(localParks);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Kaydetmede hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const handleSubstituteChange = (parkId, substituteId, substituteName) => {
    setDirty(true);
    setLocalParks(prev => prev.map(ps => {
      if (ps.park_id !== parkId) return ps;
      // Vekil KİŞİSİ değiştiyse, o parkın önceki (bir alt kademenin/başkasının
      // yazdığı) değişiklik sebebini OTOMATİK TEMİZLE; aksi halde Formen'in yazdığı
      // sebep BM/MY yazmış gibi görünüyordu. Aynı kişi tekrar seçilirse sebep korunur.
      const personChanged = String(ps.substitute_id || "") !== String(substituteId || "");
      return {
        ...ps,
        substitute_id: substituteId,
        substitute_name: substituteName,
        isChanged: true,
        change_reason: personChanged ? "" : ps.change_reason,
      };
    }));
  };

  const handleReasonChange = (parkId, reason) => {
    setDirty(true);
    setLocalParks(prev => prev.map(ps => 
      ps.park_id === parkId
        ? { ...ps, change_reason: reason, isChanged: true }
        : ps
    ));
  };

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      title="✏️ Park Vekillerini Düzenle"
      subtitle={`${personnelName}'ın parkları için vekil atama`}
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">Her park için ayrı vekil seçebilir ve değişiklik sebebi yazabilirsiniz:</p>

        {/* FAIL-CLOSED: uygunluk verisi yüklenemediyse vekil seçimi kilitli + uyarı.
            İzinli personelin yanlışlıkla vekil seçilmesini önler (fail-open değil). */}
        {eligibilityError && (
          <div className="rounded border border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid="eligibility-error">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">Vekil uygunluk bilgisi yüklenemedi.</p>
              <p className="text-xs text-red-700 mt-0.5">
                Güvenlik için vekil seçimi geçici olarak kapatıldı (izinli personel yanlışlıkla seçilmesin).
                Lütfen tekrar deneyin.
              </p>
              {onReloadEligibility && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 h-7 text-xs"
                  disabled={eligibilityLoading}
                  onClick={() => onReloadEligibility()}
                >
                  {eligibilityLoading ? "Yükleniyor..." : "Tekrar dene"}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Park listesi */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {localParks.map((ps) => (
            <ParkSubstituteItem
              key={ps.park_id}
              park={ps}
              availableSubstitutes={availableSubstitutes}
              onSubstituteChange={handleSubstituteChange}
              onReasonChange={handleReasonChange}
            />
          ))}
        </div>
        
        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            İptal
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving || updatingSubstitute || !dirty}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {(saving || updatingSubstitute) ? "Kaydediliyor..." : "💾 Kaydet"}
          </Button>
        </div>
      </div>
    </DraggableModal>
  );
};

// Tek Park Item - Kendi local state'i var (performans için)
const ParkSubstituteItem = React.memo(({ park, availableSubstitutes, onSubstituteChange, onReasonChange }) => {
  const CIM_EKIBI_OPTION_VALUE = "__cim_ekibi__";
  const CIM_EKIBI_LABEL = "ÇİM EKİBİ";
  const [localReason, setLocalReason] = useState(park.change_reason || "");

  // Parent'tan gelen yeni değerle local input'u eşitle
  React.useEffect(() => {
    setLocalReason(park.change_reason || "");
  }, [park.change_reason, park.park_id]);

  const handleSubstituteSelect = (e) => {
    const rawValue = e.target.value || null;
    const isCimEkibiOption = rawValue === CIM_EKIBI_OPTION_VALUE;
    const substituteId = isCimEkibiOption ? null : rawValue;
    const sub = availableSubstitutes.find(p => p.id === substituteId);
    if (sub?.isUnavailableForRange) return;
    onSubstituteChange(park.park_id, substituteId, isCimEkibiOption ? CIM_EKIBI_LABEL : (sub?.name || null));
  };

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">🌳</span>
        <span className="font-semibold text-amber-800">{park.park_name}</span>
      </div>
      
      {/* Vekil seçimi */}
      <div className="mb-3">
        <label className="text-xs font-medium text-gray-600 mb-1 block">Vekil:</label>
        <SearchableSelect
          value={park.substitute_id || (park.substitute_name === CIM_EKIBI_LABEL ? CIM_EKIBI_OPTION_VALUE : "")}
          onChange={(v) => handleSubstituteSelect({ target: { value: v } })}
          showAllOption={false}
          placeholder="Vekil seçin..."
          searchPlaceholder="Personel ara..."
          className="w-full text-sm bg-white"
          options={[
            { id: CIM_EKIBI_OPTION_VALUE, name: "ÇİM EKİBİ (vekil atama yok)" },
            ...availableSubstitutes.map((p) => ({
              id: p.id,
              name: `${p.name}${p.isUnavailableForRange ? ` (${p.unavailableText})` : ""}`,
              disabled: p.isUnavailableForRange,
              title: p.unavailableText || undefined,
            })),
          ]}
        />
        {availableSubstitutes.some((p) => p.isUnavailableForRange) && (
          <p className="text-[10px] text-amber-700 mt-1">
            İzinde olan personel seçime kapatıldı.
          </p>
        )}
        {(() => {
          // Mevcut vekil SONRADAN izne çıkmışsa: sessizce geçerli sayma, uyar
          // (otomatik değiştirme YOK — kullanıcı bilinçli seçsin).
          const current = availableSubstitutes.find(
            (p) => String(p.id) === String(park.substitute_id || "")
          );
          if (!current?.isUnavailableForRange) return null;
          return (
            <p className="text-[11px] font-semibold text-red-600 mt-1">
              ⚠️ Mevcut vekil ({current.name}) artık uygun değil: {current.unavailableText || "izinli"}. Lütfen başka vekil seçin.
            </p>
          );
        })()}
      </div>

      {/* Değişiklik sebebi - local state ile */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">
          Değişiklik Sebebi (Opsiyonel):
        </label>
        <input
          type="text"
          value={localReason}
          onChange={(e) => {
            const nextReason = e.target.value;
            setLocalReason(nextReason);
            // Blur beklemeden parent state'i güncelle ki Kaydet'e anında yansısın
            onReasonChange(park.park_id, nextReason);
          }}
          placeholder="Örn: İş yükü dengeleme, acil durum..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400"
        />
      </div>
    </div>
  );
});

export default LeaveRequestCard;
